/**
 * Referrals page — code, share URLs, stats, list of referrals, bonus balance.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Gift,
  Copy,
  Check,
  RefreshCw,
  Loader2,
  Twitter,
  Facebook,
  Mail,
  MessageCircle,
  Users,
  TrendingUp,
  Sparkles,
  CreditCard,
} from "lucide-react";
import AmplifierCard from "@/components/AmplifierCard";
import ShareFunnelPanel from "@/components/ShareFunnelPanel";
import ShareViaTextHero from "@/components/ShareViaTextHero";

export default function Referrals() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/referrals/me");
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load referrals");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(null), 2000);
  };

  const regenerate = async () => {
    if (!window.confirm("Burn the current code? Any existing share links will stop working immediately.")) return;
    setBusy(true);
    try {
      const { data: d } = await api.post("/referrals/regenerate");
      toast.success("New code minted.");
      setData((prev) => ({ ...prev, ...d }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't regenerate code");
    } finally { setBusy(false); }
  };

  const redeem = async () => {
    if (!window.confirm("Redeem 1 free month as Stripe credit applied to your next invoice?")) return;
    setBusy(true);
    try {
      const { data: d } = await api.post("/referrals/redeem");
      const dollars = (d.credit?.credited_cents || 0) / 100;
      toast.success(`$${dollars.toFixed(2)} credit applied — see it on your next invoice.`);
      setData((prev) => ({
        ...prev,
        bonus_months_pending: d.bonus_months_pending,
        bonus_months_consumed: d.bonus_months_consumed,
      }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't redeem credit");
    } finally { setBusy(false); }
  };

  const shareUrl = data?.social_share_url || data?.share_url;
  const shareMsg = shareUrl
    ? `Hey — been using iSpeedToClose to run my wholesale deals (AI deal analyzer, recorded calls with auto-coaching, contracts in 2 clicks). Use my link and we both get a free month: ${shareUrl}`
    : "";

  const tweet = `https://x.com/intent/tweet?text=${encodeURIComponent(shareMsg)}`;
  const facebook = shareUrl ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` : "#";
  const sms = `sms:?&body=${encodeURIComponent(shareMsg)}`;
  const mailto = `mailto:?subject=${encodeURIComponent("iSpeedToClose — free month inside")}&body=${encodeURIComponent(shareMsg)}`;

  if (loading) {
    return (
      <div className="card p-12 text-center text-[var(--ink-3)]" data-testid="referrals-loading">
        <Loader2 size={20} className="inline animate-spin" /> Loading your referrals…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div data-testid="referrals-page">
      <div className="topbar">
        <div>
          <div className="crumb">Growth</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1 flex items-center gap-2">
            <Gift size={24} className="text-amber-500" /> Give 1 month, get 1 month
          </h1>
          <p className="text-sm text-[var(--ink-3)] mt-1 max-w-2xl">
            Share your link with another wholesaler. When they upgrade to any paid plan, you both pocket a free month.
            We&rsquo;ll apply the credit on the next billing cycle.
          </p>
        </div>
      </div>

      {/* Redeem CTA — only when bonus months are pending */}
      {data.bonus_months_pending > 0 && (
        <div
          className="mb-5 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40 px-5 py-4 flex items-start gap-4"
          data-testid="referrals-redeem-card"
        >
          <div className="w-12 h-12 rounded-xl bg-white border border-emerald-200 flex items-center justify-center shrink-0">
            <Gift size={20} className="text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-extrabold text-lg leading-tight">
              You have {data.bonus_months_pending} free month{data.bonus_months_pending === 1 ? "" : "s"} banked 🎉
            </div>
            <div className="text-xs text-[var(--ink-2)] mt-1 leading-relaxed">
              Each redemption applies one month&rsquo;s subscription price as a Stripe credit
              on your next invoice. You can claim them one at a time so unused credits keep building up.
            </div>
          </div>
          <button
            type="button"
            onClick={redeem}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm transition disabled:bg-emerald-300 shrink-0"
            data-testid="referrals-redeem-btn"
          >
            {busy
              ? <><Loader2 size={14} className="animate-spin" /> Redeeming…</>
              : <><CreditCard size={14} /> Redeem 1 month</>}
          </button>
        </div>
      )}

      {/* One-tap text-share hero — the #1 conversion action on this page */}
      <ShareViaTextHero shareUrl={shareUrl} shareMsg={shareMsg} />

      {/* Amplifier celebration card — appears at 3+ paid conversions */}
      <AmplifierCard
        amplifier={data.amplifier}
        onDismissed={() => setData((d) => ({ ...d, amplifier: { ...d.amplifier, dismissed_at: new Date().toISOString() } }))}
      />

      {/* Tweeted podium-card funnel — only renders when there's activity */}
      <ShareFunnelPanel />

      {/* Stats strip */}
      <div className="grid md:grid-cols-4 gap-3 mb-5">
        <Stat icon={Users} label="Total signups" value={data.stats.total_signups} testid="ref-stat-signups" />
        <Stat icon={TrendingUp} label="Converted" value={data.stats.converted} accent testid="ref-stat-converted" />
        <Stat icon={Sparkles} label="Months banked" value={data.bonus_months_pending} highlight testid="ref-stat-bonus" />
        <Stat icon={Gift} label="Conversion rate" value={`${data.stats.conversion_rate}%`} testid="ref-stat-rate" />
      </div>

      {/* Share card */}
      <div className="card p-5 mb-5" data-testid="referrals-share-card">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider text-[var(--ink-3)]">Your referral code</div>
            <div className="font-display font-black text-3xl tracking-wider mt-1" data-testid="referrals-code">
              {data.code}
            </div>
          </div>
          <button
            type="button"
            onClick={regenerate}
            disabled={busy}
            className="btn-outline text-xs"
            data-testid="referrals-regenerate-btn"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Regenerate code
          </button>
        </div>

        <div className="space-y-2.5">
          <CopyRow
            label="Share link (preview unfurls beautifully on Twitter / iMessage)"
            value={data.social_share_url || data.share_url}
            copied={copied === "share"}
            onCopy={() => copy(data.social_share_url || data.share_url, "share")}
            testid="referrals-share-url"
          />
          <CopyRow
            label="Pre-filled signup link"
            value={data.register_url}
            copied={copied === "register"}
            onCopy={() => copy(data.register_url, "register")}
            testid="referrals-register-url"
            tone="muted"
          />
        </div>

        {data.og_image_url && (
          <div className="mt-4 pt-4 border-t border-[var(--line)]" data-testid="referrals-og-preview">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-[var(--ink-3)]">
                Your invite card preview
              </div>
              <a
                href={data.og_image_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] hover:text-[var(--primary)]"
              >
                Open full size ↗
              </a>
            </div>
            <a
              href={data.og_image_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-lg overflow-hidden border border-[var(--line)] bg-[var(--bg-2)]"
            >
              <img
                src={data.og_image_url}
                alt="Your referral invite card"
                className="w-full block"
                style={{ aspectRatio: "1200 / 630", objectFit: "cover" }}
                data-testid="referrals-og-image"
              />
            </a>
            <div className="text-[11px] text-[var(--ink-3)] mt-2 leading-relaxed">
              This is what shows up when someone unfurls your link on Twitter, iMessage, or LinkedIn — auto-personalized with your name + brand color.
            </div>
          </div>
        )}

        <div className="border-t border-[var(--line)] mt-4 pt-4">
          <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] mb-2">Share fast</div>
          <div className="flex gap-2 flex-wrap">
            <ShareBtn icon={MessageCircle} label="SMS" href={sms} testid="ref-share-sms" />
            <ShareBtn icon={Mail} label="Email" href={mailto} testid="ref-share-email" />
            <ShareBtn icon={Twitter} label="X / Twitter" href={tweet} testid="ref-share-twitter" />
            <ShareBtn icon={Facebook} label="Facebook" href={facebook} testid="ref-share-facebook" />
            <button
              type="button"
              onClick={() => copy(shareMsg, "msg")}
              className="btn-outline text-xs"
              data-testid="ref-share-copy-msg"
            >
              {copied === "msg" ? <Check size={12} /> : <Copy size={12} />}
              Copy message
            </button>
          </div>
        </div>
      </div>

      {/* Referrals list */}
      <div className="card p-5" data-testid="referrals-list-card">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-wider text-[var(--ink-3)]">
            Referrals · {data.stats.total_signups} total
          </div>
          <div className="text-xs text-[var(--ink-3)]">
            Each conversion = +1 month for both of you
          </div>
        </div>
        {data.referrals.length === 0 ? (
          <div className="text-center py-12">
            <Gift size={28} className="mx-auto text-amber-300 mb-3" />
            <div className="font-bold text-base">No referrals yet</div>
            <p className="text-sm text-[var(--ink-3)] mt-1 max-w-md mx-auto">
              Drop your link in the wholesaler Discord, Facebook group, or text it to 5 buddies. The conversion math is forgiving — even one paid sign-up pays you back in free months.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {data.referrals.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between gap-3 px-3 py-3 rounded-lg border border-[var(--line)] bg-[var(--bg-2)]"
                data-testid={`referrals-row-${r.id}`}
              >
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{r.name || "Referee"}</div>
                  <div className="text-xs text-[var(--ink-3)] truncate">
                    {r.email_masked} · joined {new Date(r.created_at).toLocaleDateString()}
                  </div>
                </div>
                {r.converted ? (
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-800 px-2.5 py-1 rounded-full">
                    <Check size={11} /> Converted · {r.tier}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-medium text-[var(--ink-3)] bg-slate-100 px-2.5 py-1 rounded-full">
                    Pending upgrade
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent, highlight, testid }) {
  return (
    <div
      className={"rounded-lg border p-3 " + (highlight ? "bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200" : accent ? "bg-emerald-50 border-emerald-200" : "card")}
      data-testid={testid}
    >
      <Icon size={14} className={highlight ? "text-amber-600 mb-1.5" : accent ? "text-emerald-600 mb-1.5" : "text-[var(--ink-3)] mb-1.5"} />
      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)]">{label}</div>
      <div className={"font-display font-extrabold text-2xl tabular-nums mt-0.5 " + (highlight ? "text-amber-700" : accent ? "text-emerald-700" : "text-[var(--ink)]")}>
        {value}
      </div>
    </div>
  );
}

function CopyRow({ label, value, copied, onCopy, testid, tone }) {
  return (
    <div className={"flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--line)] " + (tone === "muted" ? "bg-[var(--bg-2)]" : "bg-white")}>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)]">{label}</div>
        <div className="font-mono text-xs truncate text-[var(--ink-2)]" data-testid={testid}>{value}</div>
      </div>
      <button
        type="button"
        onClick={onCopy}
        className="shrink-0 inline-flex items-center gap-1 text-xs font-bold bg-[var(--ink)] text-white px-3 py-1.5 rounded-md hover:opacity-90"
      >
        {copied ? <Check size={11} /> : <Copy size={11} />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function ShareBtn({ icon: Icon, label, href, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="btn-outline text-xs"
      data-testid={testid}
    >
      <Icon size={12} />
      {label}
    </a>
  );
}
